import React, { createContext, useContext, useState, useMemo } from 'react'
import {
    useCmsOrganizationId,
    useCmsOrgIds,
    useIsCmsSuperAdmin,
} from 'entities/cms'

interface CmsOrgContextValue {
    orgId: string | null
    setOrgId: (id: string) => void
    needsOrgSelector: boolean
    /** When non-null, the selector must only offer these org IDs. Null = unrestricted (super-admin). */
    allowedOrgIds: string[] | null
}

const CmsOrgContext = createContext<CmsOrgContextValue>({
    orgId: null,
    setOrgId: () => {},
    needsOrgSelector: false,
    allowedOrgIds: null,
})

export const useCmsOrgContext = () => useContext(CmsOrgContext)

export const CmsOrgProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const resolvedOrgId = useCmsOrganizationId()
    const isSuperAdmin = useIsCmsSuperAdmin()
    const cmsOrgIds = useCmsOrgIds()

    const hasMultipleCmsOrgs = cmsOrgIds.length > 1
    const needsOrgSelector = isSuperAdmin || hasMultipleCmsOrgs
    const allowedOrgIds = hasMultipleCmsOrgs && !isSuperAdmin ? cmsOrgIds : null

    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

    const value = useMemo(
        () => ({
            orgId: needsOrgSelector ? selectedOrgId : resolvedOrgId,
            setOrgId: setSelectedOrgId,
            needsOrgSelector,
            allowedOrgIds,
        }),
        [needsOrgSelector, selectedOrgId, resolvedOrgId, allowedOrgIds]
    )

    return (
        <CmsOrgContext.Provider value={value}>
            {children}
        </CmsOrgContext.Provider>
    )
}
